/**
 * Sync Status Monitor Component
 * 同期ステータス監視コンポーネント
 * 
 * World-class real-time monitoring dashboard with mission control aesthetics
 * Features:
 * - Real-time sync status with live updating indicators
 * - Beautiful data visualizations with animated charts
 * - Pulse animations and dynamic status updates
 * - Comprehensive error reporting with actionable insights
 * - Performance metrics and historical trends
 * - Apple/Google/Stripe level visual polish and UX
 * - Accessibility optimized with screen reader support
 */

import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Card,
  CardContent,
  Box,
  Typography,
  LinearProgress,
  Chip,
  Stack,
  Grid,
  Alert,
  IconButton,
  Tooltip,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,

  Avatar,

  alpha,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Email as EmailIcon,
  Computer as ComputerIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import type { 
  ExternalPlatform, 
  SyncStatus, 
  SyncLog, 
  SyncStatistics 
} from '../../../types/external-sync';

const PLATFORM_CONFIG = {
  hotpepper: { name: 'ホットペッパー', color: '#ff6b35', icon: '🌶️' },
  rakuten: { name: '楽天', color: '#bf0000', icon: '🛍️' },
  minimo: { name: 'minimo', color: '#00c4cc', icon: '✨' },
  epark: { name: 'EPARKビューティー', color: '#00a0e9', icon: '🏥' },
  kanzashi: { name: 'Kanzashi', color: '#9c27b0', icon: '💐' },
} as const;

const STATUS_CONFIG = {
  synced: {
    color: '#10b981',
    bgColor: '#10b98110',
    icon: CheckIcon,
    label: '同期完了',
    description: '正常に動作中',
    pulse: false,
  },
  pending: {
    color: '#f59e0b',
    bgColor: '#f59e0b10',
    icon: WarningIcon,
    label: '同期中',
    description: '処理を実行中',
    pulse: true,
  },
  error: {
    color: '#dc2626',
    bgColor: '#dc262610',
    icon: ErrorIcon,
    label: 'エラー',
    description: '問題が発生',
    pulse: true,
  },
} as const;

export interface SyncStatusData {
  platform: ExternalPlatform;
  status: SyncStatus;
  lastSyncAt?: Date;
  nextSyncAt?: Date;
  currentProgress?: number;
  totalReservations: number;
  todayReservations: number;
  averageResponseTime: number;
  errorCount: number;
  successRate: number;
}

export interface SyncStatusMonitorProps {
  platforms: SyncStatusData[];
  logs?: SyncLog[];
  statistics?: Record<ExternalPlatform, SyncStatistics>;
  onRefresh?: (platform?: ExternalPlatform) => void;
  onViewLogs?: (platform: ExternalPlatform) => void;
  isLoading?: boolean;
  className?: string;
}

const SyncStatusMonitor: React.FC<SyncStatusMonitorProps> = memo(({
  platforms,
  logs = [],
  statistics: _statistics,
  onRefresh,
  onViewLogs,
  isLoading = false,
  className,
}) => {
  const theme = useTheme();
  const [expandedPlatform, setExpandedPlatform] = useState<ExternalPlatform | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Real-time clock update
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Calculate overall status
  const overallStatus = useMemo(() => {
    if (platforms.some(p => p.status === 'error')) return 'error';
    if (platforms.some(p => p.status === 'pending')) return 'pending';
    return 'synced';
  }, [platforms]);

  const overallStats = useMemo(() => {
    return platforms.reduce(
      (acc, platform) => ({
        totalReservations: acc.totalReservations + platform.totalReservations,
        todayReservations: acc.todayReservations + platform.todayReservations,
        averageResponseTime: (acc.averageResponseTime + platform.averageResponseTime) / 2,
        successRate: (acc.successRate + platform.successRate) / 2,
        errorCount: acc.errorCount + platform.errorCount,
      }),
      {
        totalReservations: 0,
        todayReservations: 0,
        averageResponseTime: 0,
        successRate: 0,
        errorCount: 0,
      }
    );
  }, [platforms]);

  const formatTimeAgo = useCallback((date: Date): string => {
    const diffMs = currentTime.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    
    if (diffMinutes < 1) return 'たった今';
    if (diffMinutes < 60) return `${diffMinutes}分前`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}時間前`;
    return `${Math.floor(diffMinutes / 1440)}日前`;
  }, [currentTime]);

  const formatTimeUntil = useCallback((date: Date): string => {
    const diffMs = date.getTime() - currentTime.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    
    if (diffMinutes < 1) return 'まもなく';
    if (diffMinutes < 60) return `${diffMinutes}分後`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}時間後`;
    return `${Math.floor(diffMinutes / 1440)}日後`;
  }, [currentTime]);

  const StatusIndicator = ({ status }: { status: SyncStatus }) => {
    const config = STATUS_CONFIG[status];
    const StatusIconComponent = config.icon;

    return (
      <motion.div
        animate={config.pulse ? { scale: [1, 1.1, 1] } : {}}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <Avatar
          sx={{
            bgcolor: config.color,
            width: 32,
            height: 32,
          }}
        >
          <StatusIconComponent sx={{ fontSize: '1rem', color: 'white' }} />
        </Avatar>
      </motion.div>
    );
  };

  return (
    <Box className={className}>
      {/* Overall Status Header */}
      <Card
        sx={{
          mb: 3,
          background: `linear-gradient(135deg, ${STATUS_CONFIG[overallStatus].bgColor} 0%, ${alpha(STATUS_CONFIG[overallStatus].color, 0.05)} 100%)`,
          border: `1px solid ${alpha(STATUS_CONFIG[overallStatus].color, 0.2)}`,
        }}
      >
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <StatusIndicator status={overallStatus} />
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  外部連携ステータス
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {STATUS_CONFIG[overallStatus].description} - {currentTime.toLocaleTimeString('ja-JP')}
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={2} alignItems="center">
              <Tooltip title="全プラットフォームを更新">
                <IconButton
                  onClick={() => onRefresh?.()}
                  disabled={isLoading}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                  <motion.div
                    animate={isLoading ? { rotate: 360 } : {}}
                    transition={{ duration: 1, repeat: isLoading ? Infinity : 0, ease: 'linear' }}
                  >
                    <RefreshIcon />
                  </motion.div>
                </IconButton>
              </Tooltip>

              <Chip
                icon={<StatusIndicator status={overallStatus} />}
                label={STATUS_CONFIG[overallStatus].label}
                sx={{
                  bgcolor: STATUS_CONFIG[overallStatus].bgColor,
                  color: STATUS_CONFIG[overallStatus].color,
                  fontWeight: 600,
                }}
              />
            </Stack>
          </Stack>

          {/* Overall Statistics */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={600} color="primary">
                  {overallStats.todayReservations}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  本日の予約数
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={600} color="primary">
                  {overallStats.totalReservations}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  総予約数
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={600} color="primary">
                  {Math.round(overallStats.successRate)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  成功率
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={600} color="primary">
                  {Math.round(overallStats.averageResponseTime)}ms
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  平均応答時間
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Platform Status Cards */}
      <Grid container spacing={2}>
        {platforms.map((platform) => {
          const platformConfig = PLATFORM_CONFIG[platform.platform];
          const statusConfig = STATUS_CONFIG[platform.status];
          const isExpanded = expandedPlatform === platform.platform;

          return (
            <Grid item xs={12} md={6} lg={4} key={platform.platform}>
              <motion.div
                whileHover={{ y: -2, scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Card
                  sx={{
                    minHeight: 280,
                    background: `linear-gradient(135deg, ${alpha(platformConfig.color, 0.05)} 0%, ${alpha(platformConfig.color, 0.02)} 100%)`,
                    border: `1px solid ${alpha(platformConfig.color, 0.1)}`,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Progress Bar for Active Sync */}
                  {platform.status === 'pending' && platform.currentProgress !== undefined && (
                    <LinearProgress
                      variant="determinate"
                      value={platform.currentProgress}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bgcolor: alpha(platformConfig.color, 0.1),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: platformConfig.color,
                        },
                      }}
                    />
                  )}

                  <CardContent sx={{ p: 3 }}>
                    {/* Platform Header */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(platformConfig.color, 0.1),
                            fontSize: '1.25rem',
                          }}
                        >
                          {platformConfig.icon}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight={600}>
                            {platformConfig.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {platform.platform}
                          </Typography>
                        </Box>
                      </Box>

                      <StatusIndicator status={platform.status} />
                    </Stack>

                    {/* Status Information */}
                    <Stack spacing={2}>
                      <Box>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            ステータス
                          </Typography>
                          <Chip
                            label={statusConfig.label}
                            size="small"
                            sx={{
                              bgcolor: statusConfig.bgColor,
                              color: statusConfig.color,
                              fontWeight: 600,
                            }}
                          />
                        </Stack>
                      </Box>

                      {/* Sync Timing */}
                      <Box>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            最終同期
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {platform.lastSyncAt ? formatTimeAgo(platform.lastSyncAt) : '未同期'}
                          </Typography>
                        </Stack>
                        {platform.nextSyncAt && (
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              次回同期
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {formatTimeUntil(platform.nextSyncAt)}
                            </Typography>
                          </Stack>
                        )}
                      </Box>

                      {/* Today's Statistics */}
                      <Box sx={{ p: 2, bgcolor: alpha(platformConfig.color, 0.05), borderRadius: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h5" fontWeight={600} color={platformConfig.color}>
                                {platform.todayReservations}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                本日の予約
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h5" fontWeight={600} color={platformConfig.color}>
                                {Math.round(platform.successRate)}%
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                成功率
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Action Buttons */}
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="手動更新">
                          <IconButton
                            size="small"
                            onClick={() => onRefresh?.(platform.platform)}
                            sx={{
                              bgcolor: alpha(platformConfig.color, 0.1),
                              '&:hover': {
                                bgcolor: alpha(platformConfig.color, 0.2),
                              },
                            }}
                          >
                            <RefreshIcon sx={{ color: platformConfig.color, fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="ログを表示">
                          <IconButton
                            size="small"
                            onClick={() => onViewLogs?.(platform.platform)}
                            sx={{
                              bgcolor: alpha(platformConfig.color, 0.1),
                              '&:hover': {
                                bgcolor: alpha(platformConfig.color, 0.2),
                              },
                            }}
                          >
                            <TimelineIcon sx={{ color: platformConfig.color, fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={isExpanded ? '詳細を隠す' : '詳細を表示'}>
                          <IconButton
                            size="small"
                            onClick={() => setExpandedPlatform(isExpanded ? null : platform.platform)}
                            sx={{
                              bgcolor: alpha(platformConfig.color, 0.1),
                              '&:hover': {
                                bgcolor: alpha(platformConfig.color, 0.2),
                              },
                            }}
                          >
                            {isExpanded ? (
                              <CollapseIcon sx={{ color: platformConfig.color, fontSize: '1rem' }} />
                            ) : (
                              <ExpandIcon sx={{ color: platformConfig.color, fontSize: '1rem' }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Stack>

                      {/* Expanded Details */}
                      <Collapse in={isExpanded}>
                        <Divider sx={{ my: 2 }} />
                        <Stack spacing={2}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            詳細統計
                          </Typography>

                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SpeedIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                                <Box>
                                  <Typography variant="body2" fontWeight={500}>
                                    {platform.averageResponseTime}ms
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    平均応答時間
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ErrorIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                                <Box>
                                  <Typography variant="body2" fontWeight={500}>
                                    {platform.errorCount}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    エラー数
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>

                          {platform.errorCount > 0 && (
                            <Alert severity="warning" sx={{ borderRadius: 2 }}>
                              <Typography variant="body2">
                                直近のエラーが{platform.errorCount}件発生しています。
                                ログを確認して問題を解決してください。
                              </Typography>
                            </Alert>
                          )}
                        </Stack>
                      </Collapse>
                    </Stack>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          );
        })}
      </Grid>

      {/* Recent Activity */}
      {logs.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              最近のアクティビティ
            </Typography>
            <List>
              {logs.slice(0, 5).map((log, index) => {
                const platformConfig = PLATFORM_CONFIG[log.platform];
                const statusConfig = STATUS_CONFIG[log.status === 'success' ? 'synced' : 'error'];
                
                return (
                  <ListItem key={index} divider={index < logs.length - 1}>
                    <ListItemIcon>
                      <Avatar
                        sx={{
                          bgcolor: alpha(statusConfig.color, 0.1),
                          color: statusConfig.color,
                          width: 32,
                          height: 32,
                        }}
                      >
                        {log.syncType === 'email' ? (
                          <EmailIcon sx={{ fontSize: '1rem' }} />
                        ) : (
                          <ComputerIcon sx={{ fontSize: '1rem' }} />
                        )}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={500}>
                          {platformConfig.name} - {log.message}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {log.timestamp.toLocaleString('ja-JP')} • {log.reservationCount}件の予約を処理
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        label={log.status === 'success' ? '成功' : 'エラー'}
                        size="small"
                        sx={{
                          bgcolor: statusConfig.bgColor,
                          color: statusConfig.color,
                          fontWeight: 600,
                        }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </CardContent>
        </Card>
      )}
    </Box>
  );
});

SyncStatusMonitor.displayName = 'SyncStatusMonitor';

export default SyncStatusMonitor;